"use client";

import React from "react";
import { cn } from "utils";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { FormattedPrice } from "../../PriceDisplay/FormattedPrice";
import NextImage from "../../NextImage";
import type {
  ChatStructuredAsset,
  ChatStructuredDisplayValue,
  ChatStructuredEarningsKpi,
  ChatStructuredEarningsTableRow,
  ChatStructuredPriceValue,
} from "./types";

import { ChatCardEyebrow } from "./common/ChatCardEyebrow";
import { ChatCardIntro } from "./common/ChatCardIntro";
import { ChatSignalTag } from "./common/ChatSignalTag";
import { ChatStructuredCardShell } from "./common/ChatStructuredCardShell";

export interface EarningsCallStructuredCardProps {
  sectionLabel: string;
  title: string;
  description: string;
  asset: ChatStructuredAsset;
  /** 如 Q2 Earnings */
  periodLabel: string;
  kpis: ChatStructuredEarningsKpi[];
  tableRows: ChatStructuredEarningsTableRow[];
  /** 表头 */
  headers?: {
    estimate: string;
    actual: string;
    result: string;
    yoy: string;
  };
  className?: string;
}

const defaultHeaders = {
  estimate: "Estimate",
  actual: "Actual",
  result: "Result",
  yoy: "YoY",
};

const isPriceValue = (
  value: ChatStructuredDisplayValue,
): value is ChatStructuredPriceValue =>
  typeof value === "object" && value !== null && "amount" in value;

export const renderDisplayValue = (
  value: ChatStructuredDisplayValue,
  textClassName: string,
) => {
  if (isPriceValue(value)) {
    return (
      <FormattedPrice
        price={String(value.amount)}
        currencySymbol={value.currencySymbol ?? "$"}
        type={value.type ?? "stock"}
        hideUnitSymbol={value.hideUnitSymbol}
        className={{ container: textClassName }}
      />
    );
  }
  return <span className={textClassName}>{value}</span>;
};

/**
 * Earnings Call — KPI 横排 + 对比表。
 */
export const EarningsCallStructuredCard: React.FC<
  EarningsCallStructuredCardProps
> = ({
  sectionLabel,
  title,
  description,
  asset,
  periodLabel,
  kpis,
  tableRows,
  headers = defaultHeaders,
  className,
}) => {
  const router = useShellRouter();
  const h = { ...defaultHeaders, ...headers };
  return (
    <ChatStructuredCardShell className={className}>
      <ChatCardEyebrow text={sectionLabel} />
      <div className="flex flex-col gap-4">
        <ChatCardIntro title={title} description={description} />
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-1">
            <div
              className={cn(
                "relative size-5 shrink-0 overflow-hidden rounded-1.5 border border-border_solid",
                asset.url && "cursor-pointer",
              )}
              data-openreplay-label="chatbox_earningscallstructuredcard_click"
              onClick={() => asset.url && router.push(asset.url)}
            >
              <NextImage
                src={asset.imageUrl}
                alt={asset.symbol}
                width={20}
                height={20}
                className="size-full object-cover"
              />
            </div>
            <div className="flex flex-wrap items-center gap-1 body_bold_m leading-5">
              <span className="text-content_primary">{asset.symbol}</span>
              <span className="body_m font-normal text-content_secondary">
                {periodLabel}
              </span>
            </div>
          </div>
          <div className="flex gap-2 rounded-2 bg-background_solid_secondary p-2">
            {kpis.map((k) => (
              <div key={k.label} className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="body_s text-content_tertiary">{k.label}</span>
                {renderDisplayValue(k.value, "title_l text-content_primary")}
                <span className="body_medium_s text-content_accent1_primary">
                  {k.changeLabel}
                </span>
              </div>
            ))}
          </div>
          <div className="flex w-full flex-col">
            <div className="flex w-full border-b border-border_white_transparent10">
              <div className="w-[82px] shrink-0 p-2" />
              <div className="flex min-w-0 flex-1 items-center justify-center p-2">
                <span className="body_s text-content_quaternary">
                  {h.estimate}
                </span>
              </div>
              <div className="flex min-w-0 flex-1 items-center justify-center p-2">
                <span className="body_s text-content_quaternary">
                  {h.actual}
                </span>
              </div>
              <div className="flex min-w-0 flex-1 items-center justify-center p-2">
                <span className="body_s text-content_quaternary">
                  {h.result}
                </span>
              </div>
              <div className="flex min-w-0 flex-1 items-center justify-center p-2">
                <span className="body_s text-content_quaternary">{h.yoy}</span>
              </div>
            </div>
            {tableRows.map((row, idx) => (
              <div
                key={`${row.metric}-${idx}`}
                className={cn(
                  "flex w-full border-b border-border_white_transparent10 last:border-b-0",
                )}
              >
                <div className="flex w-[82px] shrink-0 flex-col justify-center px-2 py-3">
                  <span className="body_m text-content_tertiary">
                    {row.metric}
                  </span>
                </div>
                <div className="flex min-h-12 min-w-0 flex-1 items-center justify-center px-2 py-3">
                  {renderDisplayValue(
                    row.estimate,
                    "body_medium_m text-content_primary",
                  )}
                </div>
                <div className="flex min-h-12 min-w-0 flex-1 items-center justify-center px-2 py-3">
                  {renderDisplayValue(
                    row.actual,
                    "body_medium_m text-content_primary",
                  )}
                </div>
                <div className="flex min-h-12 min-w-0 flex-1 items-center justify-center px-2 py-3">
                  <ChatSignalTag label={row.resultTag} compact />
                </div>
                <div className="flex min-h-12 min-w-0 flex-1 items-center justify-center px-2 py-3">
                  <span className="body_medium_m text-content_accent1_primary">
                    {row.yoyLabel}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ChatStructuredCardShell>
  );
};
